/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { SearchExploreRequest } from '@app/app/shared/models/page/page.model';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import { LinkableCardSummary, LinkablePlaylistSummary, SearchPlaylist } from '../../../shared/models';
import { GlobalSearch, GlobalSearchPlaylist } from '../../../shared/models/global-search/global-search.model';
import { Page } from '../../../shared/models/page';
import { RestClientService } from '../../../shared/services/rest-client.service';
import { ObservableResult } from '../../../shared/store';
import { GlobalSearchDataService } from './global-search-data.service';

/* eslint-disable max-len */
@Injectable()
export class ApiGlobalSearchDataService implements GlobalSearchDataService {
  constructor(
    private restClient: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  searchContent(page: number, size: number, term: string): ObservableResult<Page<GlobalSearch>> {
    return this.restClient
      .post<Page<GlobalSearch>>(
        Location.joinWithSlash(environment.apiRootUrl || '', 'search'),
        {
          term: term,
        },
        {
          page: page.toString(),
          size: size.toString(),
        },
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err) => ObservableResult.ofError(err?.error?.message)),
      );
  }

  searchExplore(page: number, size: number, request: SearchExploreRequest): ObservableResult<Page<GlobalSearchPlaylist>> {
    return this.restClient
      .post<
        Page<GlobalSearchPlaylist>
      >(Location.joinWithSlash(environment.apiRootUrl || '', `search/explore?page=${page}&size=${size}`), request)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err) => ObservableResult.ofError(err?.error?.message)),
      );
  }

  searchExploreDynamicSection(
    sectionName: string,
    page: number,
    size: number,
    request: SearchExploreRequest,
  ): ObservableResult<Page<GlobalSearchPlaylist>> {
    return this.restClient
      .post<
        Page<GlobalSearchPlaylist>
      >(Location.joinWithSlash(environment.apiRootUrl || '', `search/explore/${sectionName}?page=${page}&size=${size}`), request)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err) => ObservableResult.ofError(err?.error?.message)),
      );
  }

  searchPlaylists(page: number, term: string, type?: string): ObservableResult<Page<SearchPlaylist>> {
    const params: Record<string, string> = {
      page: page.toString(),
      size: '10',
      term: term,
    };

    if (type) {
      params.type = type;
    }

    return this.restClient.get<Page<SearchPlaylist>>(Location.joinWithSlash(environment.apiRootUrl || '', 'playlists/search'), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetPlaylists'))),
    );
  }

  searchLinkablePlaylistsV3(
    page: number,
    term: string,
    exclusions?: string[],
    pageSize: number = 10,
    types?: string[],
  ): ObservableResult<Page<LinkablePlaylistSummary>> {
    const params: Record<string, string> = {
      page: page.toString(),
      size: pageSize.toString(),
      term: term,
    };

    if (exclusions?.length) {
      params.exclusions = exclusions.join(',');
    }

    if (types?.length) {
      params.types = types.join(',');
    }

    return this.restClient
      .get<Page<LinkablePlaylistSummary>>(Location.joinWithSlash(environment.apiRootUrl || '', 'playlists/v3/linkable'), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetLinkablePlaylists'))),
      );
  }

  searchLinkablePlaylists(
    page: number,
    term: string,
    excludedId?: string,
    pageSize: number = 10,
    type?: string,
  ): ObservableResult<Page<LinkablePlaylistSummary>> {
    const params: Record<string, string> = {
      page: page.toString(),
      size: pageSize.toString(),
      term: term,
    };

    if (excludedId) {
      params.exclusions = excludedId;
    }

    if (type) {
      params.type = type;
    }

    return this.restClient
      .get<Page<LinkablePlaylistSummary>>(Location.joinWithSlash(environment.apiRootUrl || '', '/playlists/linkable'), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetLinkablePlaylists'))),
      );
  }

  searchLinkableCards(
    page: number,
    term: string,
    exclusions?: string[],
    pageSize: number = 10,
    cardType?: string,
  ): ObservableResult<Page<LinkableCardSummary>> {
    const params: Record<string, string> = {
      page: page.toString(),
      size: pageSize.toString(),
      term: term,
    };

    if (exclusions?.length) {
      params.exclusions = exclusions.join(',');
    }

    if (cardType) {
      params.type = cardType;
    }

    return this.restClient
      .get<Page<LinkableCardSummary>>(Location.joinWithSlash(environment.apiRootUrl || '', 'cards/linkable'), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetLinkableCards'))),
      );
  }

  searchLinkableCardsV3(
    page: number,
    term: string,
    exclusions?: string[],
    pageSize: number = 10,
    cardType?: string,
  ): ObservableResult<Page<LinkableCardSummary>> {
    const params: Record<string, string> = {
      page: page.toString(),
      size: pageSize.toString(),
      term: term,
    };

    if (exclusions?.length) {
      params.exclusions = exclusions.join(',');
    }

    if (cardType) {
      params.type = cardType;
    }

    return this.restClient
      .get<Page<LinkableCardSummary>>(Location.joinWithSlash(environment.apiRootUrl || '', 'cards/v3/linkable'), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetLinkableCards'))),
      );
  }
}
